import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  ELSCommonConfig,
  ELSCommonUIConstants,
  ELSStorageHelper
} from '@els/els-ui-common-react';
import {
  merge,
  noop
} from 'lodash';
import { PERSIST_KEY } from './app.constants';
import { AppState } from './app-state.types';
import { DefaultAppState } from './app-state.constants';
import { getChatModeTypeLocalStorageKey } from '../../pages/ai-chat/ai-chat.utilities';
import { getDefaultAdobeAnalyticsPageData } from '../analytics-registrar/analytics-registrar.utilities';

type AppProviderProps = {
  children: React.ReactNode;
}

export type AppContextState = {
  appState: AppState;
  setAppState: React.Dispatch<React.SetStateAction<AppState>>;
  appStateRef: React.MutableRefObject<AppState>;
}

const defaultContextValue: AppContextState = {
  appState: DefaultAppState,
  setAppState: noop,
  appStateRef: { current: DefaultAppState },
};

export const AppContext: React.Context<AppContextState> = createContext(defaultContextValue);

const AppProvider = ({ children }: AppProviderProps): JSX.Element => {

  const storedAppState = ELSStorageHelper.driver(ELSCommonConfig.storageDriver)
    .namespace('')
    .get(PERSIST_KEY);

  const [appState, setAppState] = useState({
    ...DefaultAppState,
    ...storedAppState,
    pendingRequestCount: 0,
    isLoading: false
  });

  const appStateRef = useRef(appState);
  const prevChatModeTypeRef = useRef(appState.chatModeType);

  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  useEffect(() => {
    ELSStorageHelper.driver(ELSCommonConfig.storageDriver)
      .namespace('')
      .put(PERSIST_KEY, appState);
    // Only write chatModeType when it changes — writing on every appState update would overwrite
    // the stored value before AppLinkRedirect has a chance to read and restore it on reload.
    if (appState.chatModeType !== prevChatModeTypeRef.current) {
      const storageKey = getChatModeTypeLocalStorageKey(appState.appLinkData, appState.userId);
      if (storageKey) {
        ELSStorageHelper
          .driver(ELSCommonUIConstants.security.Locker.DefaultDriver)
          .namespace('')
          .put(storageKey, appState.chatModeType);
      }
    }
    prevChatModeTypeRef.current = appState.chatModeType;
    merge(window.pageData, getDefaultAdobeAnalyticsPageData(appState));
  }, [appState]);

  const contextValue = useMemo(() => ({
    appState,
    setAppState,
    appStateRef,
  }), [
    appState,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
